import React, { type ReactNode, type ComponentProps } from 'react'
import { Grid, Row, useContainerWidth } from '@fs/zion-ui'
import { type SummaryPerson } from '@fs/zion-tree-types'
import { HeightDiv } from './providers/HeightProvider'
import { type PersonType } from './types/general'
import PersonAndDateAdded from './PersonAndDateAdded'

type PersonSwitchContainerProps = {
  isMobileOrientation: boolean
  children: ReactNode
}

function PersonSwitchContainer({ isMobileOrientation, children }: PersonSwitchContainerProps): JSX.Element {
  return isMobileOrientation ? (
    <Grid gutters="nano">{children}</Grid>
  ) : (
    <Row gutters="nano" alignY="middle">
      {children}
    </Row>
  )
}

export const heightDivTypeMap: Record<PersonType, ComponentProps<typeof HeightDiv>['type']> = {
  duplicate: 'compare1',
  survivor: 'compare2',
  resultant: 'compare3',
}

type Props = {
  person: Partial<SummaryPerson> | undefined
  personType: PersonType
  personProps?: Record<string, unknown>
  id: ComponentProps<typeof HeightDiv>['id']
}

export default function PersonCardContent({ personType, person, personProps, id }: Props): JSX.Element {
  const atWidth = useContainerWidth()
  const isMobileOrientation = atWidth({ default: true, nano: false })

  return (
    <HeightDiv id={id} type={heightDivTypeMap[personType]}>
      <PersonSwitchContainer isMobileOrientation={isMobileOrientation}>
        <PersonAndDateAdded
          avatarSize="smaller"
          componentSize={isMobileOrientation ? 'sm' : 'md'}
          openPersonOverlay
          disableTruncation={!isMobileOrientation}
          isMobileOrientation={isMobileOrientation}
          lifespanSize="short"
          {...personProps}
          personObj={person}
        />
      </PersonSwitchContainer>
    </HeightDiv>
  )
}

export function PersonCardContentSkeleton(): JSX.Element {
  const atWidth = useContainerWidth()
  const isMobileOrientation = atWidth({ default: true, nano: false })

  return (
    <PersonSwitchContainer isMobileOrientation={isMobileOrientation}>
      <PersonAndDateAdded
        avatarSize="smaller"
        componentSize={isMobileOrientation ? 'sm' : 'md'}
        disableTruncation={!isMobileOrientation}
        isMobileOrientation={isMobileOrientation}
        lifespanSize="short"
        loading
        personObj={undefined}
      />
    </PersonSwitchContainer>
  )
}
